import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { sendSentryLoginSmokeTest } from '@/sentry';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Lock, Mail, Phone } from 'lucide-react';
import reserveoLogo from '../reserveo.png';

export default function Login() {
  const [loginType, setLoginType] = useState('email'); // 'email' o 'phone'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSentryTest = () => {
    const result = sendSentryLoginSmokeTest();
    if (!result.ok) {
      toast.error(result.reason);
      return;
    }
    toast.success(
      result.eventId
        ? `Evento enviado a Sentry (id: ${result.eventId}). Revisa el panel.`
        : 'Evento enviado a Sentry. Revisa el panel en unos segundos.'
    );
  };

  const handlePhoneChange = (e) => {
    // Solo permitir números y máximo 10 dígitos
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (loginType === 'email') {
        await login(email, password, 'email');
      } else {
        await login(phone, password, 'phone');
      }
      toast.success('Bienvenido a Reserveo');
      navigate('/');
    } catch (error) {
      // Mejor manejo de errores
      let errorMessage = 'Error al iniciar sesión';
      
      if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error')) {
        errorMessage = 'No se pudo conectar con el servidor. Verifica que el backend esté ejecutándose.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        // Laravel validation errors
        const firstError = Object.values(error.response.data.errors)[0];
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        duration: 5000, // 5 segundos para errores de conexión
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#335C67]/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#E09F3E]/5 rounded-full blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md relative animate-scale-up border-slate-100 shadow-[0_12px_32px_-8px_rgba(43,45,66,0.12)]">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-20 h-20 flex items-center justify-center">
            <img
              src={reserveoLogo}
              alt="Reserveo"
              className="max-h-60 w-auto object-contain drop-shadow-md"
              style={{maxWidth:'80px'}}
            />
          </div>
          <div>
            <CardDescription className="text-[#5D5C61] mt-1">
              Agenda de citas para servicios
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <Tabs value={loginType} onValueChange={setLoginType} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="email">Correo</TabsTrigger>
              <TabsTrigger value="phone">Teléfono</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <TabsContent value="email" className="space-y-5 mt-0">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2B2D42] font-medium">
                    Correo electrónico
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8D99AE]" />
                    <Input
                      id="email"
                      type="email"
                      placeholder=""
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-11 h-12 bg-white border-slate-200 focus:ring-2 focus:ring-[#335C67]/20 focus:border-[#335C67]"
                      data-testid="login-email-input"
                      required={loginType === 'email'}
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="phone" className="space-y-5 mt-0">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#2B2D42] font-medium">
                    Teléfono (10 dígitos)
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8D99AE]" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="8123456789"
                      value={phone}
                      onChange={handlePhoneChange}
                      className="pl-11 h-12 bg-white border-slate-200 focus:ring-2 focus:ring-[#335C67]/20 focus:border-[#335C67]"
                      data-testid="login-phone-input"
                      required={loginType === 'phone'}
                      maxLength={10}
                    />
                  </div>
                  <p className="text-xs text-[#8D99AE]">Ingresa solo los 10 dígitos (sin LADA)</p>
                </div>
              </TabsContent>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#2B2D42] font-medium">
                  Contraseña
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8D99AE]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 h-12 bg-white border-slate-200 focus:ring-2 focus:ring-[#335C67]/20 focus:border-[#335C67]"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 bg-[#335C67] hover:bg-[#244048] text-white rounded-full font-medium transition-all active:scale-95"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full h-10 rounded-full border-dashed border-slate-300 text-xs text-[#5D5C61] hover:bg-slate-50"
                onClick={handleSentryTest}
              >
                Probar Sentry (monitoreo)
              </Button>
            </form>
          </Tabs>
          
          <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
            <p className="text-center text-sm text-[#8D99AE]">
              ¿No tienes cuenta?{' '}
              <Link to="/agendar" className="text-[#335C67] hover:text-[#244048] font-medium">
                Reservar sin registro
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
      
      <footer className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-sm text-[#8D99AE]">
          Desarrollado por{' '}
          <a
            href="https://agsoftweb.com.mx"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#335C67] hover:text-[#244048] font-medium transition-colors"
          >
            Agsoftweb
          </a>
        </p>
      </footer>
    </div>
  );
}
